<template>
	<div id="songs">
		<div class="song"
			 :data-song="song.songId"
			 :draggable="canSort"
			 @touchstart="touchStart(song.songId)"
			 @touchmove="touchMove"
			 @touchend="drop"
			 @dragstart="dragStart"
			 @dragover="dragOver"
			 @drop="drop"
			 v-for="song in songs" v-bind:key="song.id"
			 @click="selectSong(song)"
			 @mousedown="handleMouseDown"
			 @mouseup="handleMouseUp"
			 :class="{ active: currentSong !== null && currentSong.id === song.id, 'sort-drop': song.songId === sorting.dropId, sorting: songsSorting }">
			<template v-for="playlistId in songsPlaylists[song.songId]">
				<span v-bind:key="`${song.songId}-${playlistId}`" class="playlist-mark" :style="playlistMarkStyle(playlistId)" v-if="isShowPlaylistMark(playlistId)"></span>
			</template>
			{{ song.title }}
			<small>{{ song.author }}</small>
		</div>
	</div>
</template>

<script>
	import { mapState } from "vuex";
	import { SongsSortingMixin } from "@/mixins/SongsSortingMixin";
	import { orderBy } from "natural-orderby";
	import {
		ARROW_KEYS_UP_DOWN,
		ARROW_KEYS_UP_DOWN__SWITCH_SONGS,
		ControlService
	} from "@/services/ControlService";

	export default {
		components: {
		},
		data() {
			return {
				currentSongIndex: null,
				nextSongPrepared: null
			};
		},
		mixins: [
			SongsSortingMixin
		],
		computed: {
			...mapState([
				"currentSong",
				"externalSearchOpened",
				"songsFilter",
				"rootSideOpened",
				"playlists",
				"authors",
				"authorsSongs",
				"queueSongs",
				"queueSongsOrders",
				"playlistsSongsOrders",
				"currentSong",
				"localSearchOpened",
				"playlistsSongs",
				"songsFilterNonPlayed",
				"songsSorting",
				"songsPlaylists"
			]),
			canSort() {
				return this.songsFilter.playlist !== null || this.songsFilter.onlyQueue;
			}
		},
		recomputed: {
			songsIds() {
				if (this.songsFilter.playlist !== null) {
					return this.playlistsSongs[this.songsFilter.playlist].sort((a, b) => {
						return this.playlistsSongsOrders[this.songsFilter.playlist][a] - this.playlistsSongsOrders[this.songsFilter.playlist][b];
					});

				} else if (this.songsFilter.author !== null) {
					return this.authorsSongs[this.songsFilter.author];

				} else if (this.songsFilter.onlyQueue) {
					return this.queueSongs.sort((a, b) => {
						return this.queueSongsOrders[a] - this.queueSongsOrders[b];
					});

				} else if (!this.songsFilter.onlyQueue) {
					return Object.keys(this.$store.state.songs);
				}

				return [];
			},
			songs() {
				var songs = this.songsIds
					.filter(id => this.$store.state.songs[id] !== undefined)
					.map(id => this.$store.state.songs[id]);
				if (this.songsFilterNonPlayed) {
					const date = Date.now() - (60 * 60 * 12 * 1000);
					songs = songs.filter(song => !("lastPlayedDatetime" in song) || song.lastPlayedDatetime < date);
				}

				if (!(this.songsFilter.playlist !== null || this.songsFilter.onlyQueue === true)) {
					songs = orderBy(songs, v => v.title);
				}

				return songs;
			}
		},
		methods: {
			isShowPlaylistMark(playlistId) {
				if (this.songsFilter.playlist === playlistId) return false;
				if (!(playlistId in this.playlists)) return false;
				const playlist = this.playlists[playlistId];
				if (playlist === undefined) return false;
				if (!("color" in playlist)) return false;
				return playlist.color !== null;
			},
			playlistMarkStyle(playlistId) {
				return this.isShowPlaylistMark(playlistId) ? {
					"background-color": this.playlists[playlistId].color
				} : {};
			},
			selectSong(song) {
				this.$store.commit("setCurrentSong", song);
				this.$router.push({ name: "zpevnik-song", params: { song: song.songId } });
				this.processCurrentSongIndex();
				document.dispatchEvent(new Event("ScrollSongTop"));
			},
			processCurrentSongIndex() {
				const songsIds = this.songs.slice(0).map(s => s.songId);
				if (this.currentSong !== null) {
					if (!(this.currentSongIndex !== null && typeof songsIds[this.currentSongIndex] !== "undefined" && songsIds[this.currentSongIndex] === this.currentSong.id)) {
						this.currentSongIndex = songsIds.indexOf(this.currentSong.songId);
					}
				}
			},
			keydownEvent(e) {
				if (this.localSearchOpened) return;
				if (e.srcElement.tagName === "TEXTAREA") return;
				if (!(e.code === "ArrowDown" || e.code === "ArrowUp")) return;
				if (ControlService.getSettings(ARROW_KEYS_UP_DOWN) !== ARROW_KEYS_UP_DOWN__SWITCH_SONGS) {
					return;
				}
				e.preventDefault();

				this.processCurrentSongIndex();

				const songsIds = this.songs.slice(0).map(s => s.songId);
				if (this.currentSongIndex === -1 || this.currentSongIndex === null) {
					this.currentSongIndex = 0;
					this.selectSong(this.$store.state.songs[songsIds[this.currentSongIndex]]);
					return;
				}

				if (e.code === "ArrowDown") {
					this.nextSong();
				} else if (e.code === "ArrowUp") {
					this.prevSong();
				}
			},
			nextSong() {
				const songsIds = this.songs.slice(0).map(s => s.songId);

				if (this.currentSongIndex >= 0 && this.currentSongIndex < songsIds.length - 1) {
					this.currentSongIndex += 1;
					this.selectSong(this.$store.state.songs[songsIds[this.currentSongIndex]]);
				}
			},
			prevSong() {
				const songsIds = this.songs.slice(0).map(s => s.songId);

				if (this.currentSongIndex >= 1) {
					this.currentSongIndex -= 1;
					this.selectSong(this.$store.state.songs[songsIds[this.currentSongIndex]]);
				}
			},
			refreshSongs() {
				this.$recompute("songsIds");
			},
			prepareRouteToNextSong() {
				this.nextSongPrepared = this.getNextSong();
			},
			routeToNextSong() {
				if (this.nextSongPrepared) {
					this.selectSong(this.nextSongPrepared);
					this.nextSongPrepared = null;
				}
			},
			getNextSong() {
				let next = false;

				const songs = this.songs.slice(0);
				for (var i in songs) {
					if (next) {
						return songs[i];
					}
					if (songs[i].songId === this.currentSong.songId) {
						next = true;
					}
				}
				next = false;
				for (i in songs.reverse()) {
					if (next) {
						return songs[i];
					}
					if (songs[i].songId === this.currentSong.songId) {
						next = true;
					}
				}
			}
		},
		mounted() {
			document.addEventListener("keydown", this.keydownEvent);
			document.addEventListener("NextSong", this.nextSong);
			document.addEventListener("PrevSong", this.prevSong);
			document.addEventListener("RefreshSongs", this.refreshSongs);
			document.addEventListener("PrepareRouteToNextSong", this.prepareRouteToNextSong);
			document.addEventListener("RouteToNextSong", this.routeToNextSong);
		},
		destroyed() {
			document.removeEventListener("keydown", this.keydownEvent);
			document.removeEventListener("NextSong", this.nextSong);
			document.removeEventListener("PrevSong", this.prevSong);
			document.removeEventListener("RefreshSongs", this.refreshSongs);
			document.removeEventListener("PrepareRouteToNextSong", this.prepareRouteToNextSong);
			document.removeEventListener("RouteToNextSong", this.routeToNextSong);
		}
	}

</script>

<style lang="scss" scoped>
	#songs {
		.song {
			cursor: pointer;
			background-color: rgba(180, 177, 186, 0.18);
			margin-bottom: 1px;
			padding: 7px 15px 7px 15px;
			line-height: 30px;
			color: rgba(255, 255, 255, 0.9);
			font-weight: 300;

			&.active {
				background-color: rgba(39, 34, 26, 0.2);

				&:hover {
					background-color: rgba(39, 34, 26, 0.2);
				}
			}

			&.sort-drop {
				border-top: 6px dotted #e59253;
			}

			&.sorting {
				cursor: move !important;
			}

			@media not all and (pointer: coarse) {
				&:hover {
					background-color: rgba(180, 177, 186, 0.33);
				}
			}

			span.playlist-mark {
				position: absolute;
				display: none;
				margin-left: -13px;
				border-top-right-radius: 4px;
				border-bottom-right-radius: 4px;
				width: 5px;
				height: 18px;
				background-color: #e7ffaa;

				&:nth-child(1) {
					margin-top: 6px;
					display: block;
				}

				&:nth-child(2) {
					margin-top: 30px;
					display: block;
				}
			}
		}

		.song > small {
			display: block;
			opacity: 0.5;
			line-height: 20px;
		}
	}
</style>
